import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO head fragment — title, description, canonical, OG tags,
 * hreflang alternates, and optional JSON-LD schema.
 *
 * Place at the top of any page. Builds the canonical URL from the current
 * window location so it's robust to preview-vs-prod hosts.
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  alternates = [],
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} — Velcor Energy` : "Velcor Energy";
  const origin =
    (typeof window !== "undefined" && window.location.origin) ||
    process.env.REACT_APP_BACKEND_URL ||
    "";
  const canonicalUrl = canonical
    ? canonical.startsWith("http")
      ? canonical
      : `${origin}${canonical}`
    : (typeof window !== "undefined" && window.location.href) || "";
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${origin}${ogImage}`
    : `${origin}/logo.png`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {alternates.map((alt) => (
        <link
          key={alt.hreflang}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href.startsWith("http") ? alt.href : `${origin}${alt.href}`}
        />
      ))}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
